/**
 * @fileoverview Teacher Signup API service
 * @description API calls for teacher signup flow
 */

import { api, EmailAlreadyRegisteredError } from 'helpers'
import axios from 'axios'
import { ARC_MOSAICO_ENDPOINTS } from 'api/arcMosaico'
import type {
  School,
  Grade,
  SignupData,
  SignupApiResponse,
  SignupRequest,
  SignupApiError,
  FirebaseUser,
  FirebaseTokenManager,
  OnboardingRequest,
  OnboardingResponse,
  TutorInviteValidation,
  TutorInviteSignupRequest,
  TutorInviteSignupResponse,
} from './types'

/**
 * Get available grade options from grades endpoint
 * @returns List of grades
 */
export const getGrades = async (): Promise<Grade[]> => {
  const response = await api.get<Array<{ id: number; name: string }>>(ARC_MOSAICO_ENDPOINTS.grades.list)
  return response.data.map(grade => ({
    value: String(grade.id),
    label: grade.name,
  }))
}

/**
 * API response type for external schools
 */
interface ExternalSchoolResponse {
  id: string
  name: string
  city: string | null
  state: string | null
  zipcode: string | null
  street: string | null
}

/**
 * Map API response to School type
 */
const mapSchoolResponse = (school: ExternalSchoolResponse): School => ({
  id: String(school.id),
  name: school.name,
  city: school.city || '',
  state: school.state || '',
  zipcode: school.zipcode || '',
  street: school.street || '',
})

/**
 * Get external schools list (initial load)
 * @returns List of external schools
 */
export const getExternalSchools = async (): Promise<School[]> => {
  const response = await api.get<ExternalSchoolResponse[]>(ARC_MOSAICO_ENDPOINTS.externalSchools.list)
  return response.data.map(mapSchoolResponse)
}

/**
 * Search external schools by name
 * @param query - Search query
 * @returns List of matching schools
 */
export const searchExternalSchools = async (query: string): Promise<School[]> => {
  if (!query || query.length < 2) {
    return getExternalSchools()
  }
  const response = await api.get<ExternalSchoolResponse[]>(
    ARC_MOSAICO_ENDPOINTS.externalSchools.search,
    { params: { q: query, limit: 50 } },
  )
  return response.data.map(mapSchoolResponse)
}

/**
 * Create a new external school
 * @param name - School name
 * @param zipcode - School zipcode
 * @returns Created school
 */
export const createExternalSchool = async (name: string, zipcode: string): Promise<School> => {
  const response = await api.post<{ id: number; name: string; zipcode: string }>(
    ARC_MOSAICO_ENDPOINTS.externalSchools.create,
    { externalSchool: { name, zipcode } },
  )
  return {
    id: String(response.data.id),
    name: response.data.name,
    city: '',
    state: '',
    zipcode: response.data.zipcode || '',
    street: '',
  }
}

/**
 * Check if errors indicate email is already registered
 */
const isEmailAlreadyRegistered = (errors: Record<string, string[]>): boolean => {
  const emailErrors = errors?.email
  if (!Array.isArray(emailErrors)) return false
  return emailErrors.some(msg =>
    msg.toLowerCase().includes('already exists') ||
    msg.toLowerCase().includes('has already been taken')
  )
}

/**
 * Formats error object into readable message
 * Input: { email: ["already exists"], password: ["is too short"] }
 * Output: "email already exists, password is too short"
 * @throws EmailAlreadyRegisteredError when email already exists
 */
const formatErrorMessages = (errors: Record<string, string[]>): string => {
  if (!errors || typeof errors !== 'object') return 'Signup failed'
  if (isEmailAlreadyRegistered(errors)) throw new EmailAlreadyRegisteredError()
  return Object.entries(errors)
    .map(([field, messages]) => `${field} ${messages.join(', ')}`)
    .join(', ')
}

/**
 * Submit teacher signup using the unified signup endpoint
 * POST /api/arc_mosaico/auth/signup
 * @throws EmailAlreadyRegisteredError when email already exists
 * @throws Error with validation messages for other failures
 */
export const submitSignup = async (data: SignupData): Promise<SignupApiResponse> => {
  const payload: SignupRequest = {
    email: data.email,
    password: data.password,
    firstName: data.firstName,
    lastName: data.lastName,
  }

  try {
    const response = await api.post<SignupApiResponse>(ARC_MOSAICO_ENDPOINTS.auth.signup, payload)
    return response.data
  } catch (error: unknown) {
    const responseData = (error as { response?: { data?: Partial<SignupApiError> & { message?: string } } })
      .response?.data
    if (responseData?.errors) throw new Error(formatErrorMessages(responseData.errors))
    if (responseData?.message) throw new Error(responseData.message)
    throw new Error('Signup failed. Please check your connection and try again.')
  }
}

/**
 * Validate a tutor invitation token
 * GET /api/arc_mosaico/tutor_invites/validate/{token}
 * @param token - Invitation token from URL
 * @returns Validation result with tutor and school info
 */
export const validateTutorInvite = async (token: string): Promise<TutorInviteValidation> => {
  try {
    const response = await api.get<TutorInviteValidation>(
      ARC_MOSAICO_ENDPOINTS.tutorInvites.validate(token),
    )
    return response.data
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 422) {
      const data = error.response.data as TutorInviteValidation
      return { valid: false, error: data.error || 'Invalid or expired invitation token' }
    }
    throw new Error('Failed to validate invitation token')
  }
}

/**
 * Submit tutor invite signup (set password)
 * POST /api/arc_mosaico/auth/tutor_invite_signup
 * @param data - Invitation token, email, password and confirmation
 * @returns Signup response with user data
 */
export const submitTutorInviteSignup = async (
  data: TutorInviteSignupRequest,
): Promise<TutorInviteSignupResponse> => {
  try {
    const response = await api.post<TutorInviteSignupResponse>(
      ARC_MOSAICO_ENDPOINTS.auth.tutorInviteSignup,
      data,
    )
    return response.data
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response) {
      const { response: errResponse } = error
      if (errResponse.status === 422) {
        const errorData = errResponse.data as { errors?: string | string[] }
        if (errorData.errors) {
          const message = Array.isArray(errorData.errors)
            ? errorData.errors.join(', ')
            : errorData.errors
          throw new Error(message)
        }
      }
      const message = (errResponse.data as { message?: string })?.message
      if (message) {
        throw new Error(message)
      }
    }
    throw new Error('Failed to complete signup')
  }
}

/** Google signup payload structure */
interface GoogleSignupPayload {
  firebaseUser: {
    email: string
    uid: string
    displayName?: string
    firstName?: string
    lastName?: string
  }
  session?: {
    firebaseUser: {
      stsTokenManager: {
        expirationTime: number
        accessToken: string
        refreshToken: string
      }
    }
  }
}

/**
 * Submit Google signup using Firebase authentication
 * Note: Uses axios directly because backend expects camelCase params (firebaseUser)
 * but the api helper converts to snake_case
 *
 * @param firebaseUser - User data from Firebase Google OAuth
 * @param tokenManager - Firebase token manager with access token
 * @returns Signup API response with user data
 * @throws Error with validation messages if signup fails
 */
export const submitGoogleSignup = async (
  firebaseUser: FirebaseUser,
  tokenManager?: FirebaseTokenManager,
): Promise<SignupApiResponse> => {
  // Backend expects camelCase: firebaseUser, not firebase_user
  // So we use axios directly instead of api helper (which converts to snake_case)
  const payload: GoogleSignupPayload = {
    firebaseUser: {
      email: firebaseUser.email,
      uid: firebaseUser.uid,
      displayName: firebaseUser.displayName,
      firstName: firebaseUser.firstName,
      lastName: firebaseUser.lastName,
    },
  }

  // Add token if available for backend validation
  if (tokenManager) {
    payload.session = {
      firebaseUser: {
        stsTokenManager: {
          expirationTime: tokenManager.expirationTime,
          accessToken: tokenManager.accessToken,
          refreshToken: tokenManager.refreshToken,
        },
      },
    }
  }

  try {
    const response = await axios.post<SignupApiResponse>(ARC_MOSAICO_ENDPOINTS.auth.googleSignup, payload)
    return response.data
  } catch (error: unknown) {
    // Handle 422 errors (validation errors)
    if (axios.isAxiosError(error) && error.response?.status === 422) {
      const errorData = error.response.data as SignupApiError
      throw new Error(formatErrorMessages(errorData.errors))
    }
    throw new Error('Unexpected error during Google signup')
  }
}

/**
 * Complete user onboarding with school and grade selection
 * Creates: Account, School, SchoolYear, Group
 *
 * @param externalSchoolId - ID of the selected external school
 * @param gradeIds - IDs of the selected grades
 * @returns Onboarding response with created entities
 * @throws Error with validation messages if onboarding fails
 */
export const completeOnboarding = async (
  externalSchoolId: string,
  gradeIds: string[],
): Promise<OnboardingResponse> => {
  // Validate inputs - IDs are UUIDs (strings)
  if (!externalSchoolId || typeof externalSchoolId !== 'string') {
    throw new Error('School is required')
  }
  if (!gradeIds || gradeIds.length === 0) {
    throw new Error('At least one grade is required')
  }

  // Use camelCase - api helper converts to snake_case automatically
  const payload: OnboardingRequest = {
    externalSchoolId,
    gradeIds,
  }

  try {
    const response = await api.post<OnboardingResponse>(
      ARC_MOSAICO_ENDPOINTS.setup.completeOnboarding,
      payload,
    )

    // Handle 422 errors that are returned (not thrown) by api interceptor
    if (response.status === 422 || (response.data as any)?.success === false) {
      const errorData = response.data as unknown as SignupApiError
      throw new Error(formatErrorMessages(errorData.errors))
    }

    return response.data
  } catch (error: unknown) {
    // Re-throw if already processed
    if (error instanceof Error) {
      throw error
    }
    // Handle axios errors (network, etc)
    if (axios.isAxiosError(error) && error.response?.status === 422) {
      const errorData = error.response.data as SignupApiError
      throw new Error(formatErrorMessages(errorData.errors))
    }
    throw new Error('Failed to complete onboarding')
  }
}

